<template>
  <v-card flat>
    <v-card-title>Plans</v-card-title>
    <v-card-text>
      <v-toolbar flat>
        <DebouncedTextField
            label="search"
            v-model="search"
            clearable
        />
        <v-spacer/>
        <v-btn outlined @click="addNewPlan">New Plan</v-btn>
      </v-toolbar>
      <!-- the box rule (fed-lib ordersAsBox): a plan orders as a BOX when its
           stream's record says so (streams/<id>.box) and it MUST then price by
           box_sizes — a box-stream plan without sizes is refused by fed-lib,
           box_sizes on a non-box stream are never used -->
      <v-alert v-if="planWarnings.length" type="warning" outlined dense>
        <div class="font-weight-bold">Plan / stream box settings need attention</div>
        <div v-for="w of planWarnings" v-bind:key="w.id"><b>{{ w.id }}</b>: {{ w.text }}</div>
      </v-alert>
      <v-data-table
          :search="search"
          :items="planList"
          :headers="headers"
          disable-pagination
          hide-default-footer
      >
        <template v-slot:item.action="{item}">
          <v-btn outlined small @click="editPlan(item)">Edit</v-btn>
        </template>
        <template v-slot:item.box="{item}">
          <v-chip v-if="warningFor(item)" small color="warning" :title="warningFor(item)">
            <v-icon small left>mdi-alert</v-icon>
            {{ warningFor(item) }}
          </v-chip>
          <v-chip v-else-if="isBoxStream(item)" small outlined>box</v-chip>
        </template>
        <template v-slot:item.ranges="{item : { ranges}}">
          <div v-for="(r,i) of (
            ranges &&
            ranges
              .filter(r => !!r)
              .map(r => `${r.price} : ${r.description}` + (r.free_shipping ? '(free shipping)' : ''))
              )" v-bind:key="i">
            {{ r }}
          </div>
        </template>
        <template v-slot:item.adjust="{item : { adjust}}">
          {{ adjust }}
        </template>
      </v-data-table>

      <BoxPlanEditor v-model="boxEditor" :plan-id="boxEditorPlanId"/>

    </v-card-text>
  </v-card>
</template>

<script>
import {mapActions, mapState} from "vuex";
import urlState from "@/router/urlState";
import DebouncedTextField from "@/components/DebouncedTextField.vue";
import BoxPlanEditor from "@/components/BoxPlanEditor.vue";


export default {
  name: "Plans",
  components: {DebouncedTextField, BoxPlanEditor},
  mixins: [urlState],
  mounted() {
    this.syncToUrl({
      param: 'search', urlParam: 'search', initFromRoute: true,
    });
    return Promise.all([this.fetchPlans(), this.fetchStreams()]);
  },
  methods: {
    ...mapActions(['fetchPlans', 'fetchStreams', 'updatePlan']),
    // the plan's stream record says whether its orders are boxes
    isBoxStream(plan) {
      const stream = plan && plan.stream && this.streams && this.streams[plan.stream];
      return !!(stream && stream.box);
    },
    hasBoxSizes(plan) {
      return !!(plan && Array.isArray(plan.box_sizes) && plan.box_sizes.length > 0);
    },
    // what is wrong with a plan's box settings, or null
    warningFor(plan) {
      if (!plan || !this.streams) return null;
      if (plan.stream && !this.streams[plan.stream]) {
        return `stream "${plan.stream}" does not exist`;
      }
      if (this.isBoxStream(plan) && !this.hasBoxSizes(plan)) {
        return `on box stream "${plan.stream}" but has no box_sizes — fed-lib refuses this plan`;
      }
      if (!this.isBoxStream(plan) && this.hasBoxSizes(plan)) {
        return `has box_sizes but stream "${plan.stream || '(none)'}" is not a box stream (streams/<id>.box) — orders as meals, sizes unused`;
      }
      return null;
    },
    editPlan(plan) {
      // a plan on the box STREAM has its own editor (box sizes, filters,
      // surcharges); any other stream uses the generic plan editor
      if (this.isBoxStream(plan)) {
        this.boxEditorPlanId = plan.id;
        this.boxEditor = true;
      } else {
        this.$router.push({name: 'PlanEdit', params: {id: plan.id}});
      }
    },
    addNewPlan() {
      let planId = prompt('Enter the id of the new plan: (no spaces, lower case)') || '';
      planId = planId.toLowerCase().replaceAll(/[\W\s]/g, '');
      if (planId) {
        if (this.plans[planId]) {
          alert('a plan already exists with the plan id ' + planId);
        } else {
          return this
              .updatePlan({id: planId, update: {}})
              .then(() => this.fetchPlans({force: true}))
              .then(() => this.$router.push({name: 'PlanEdit', params: {id: planId}}));
        }
      }
    }
  },
  computed: {
    ...mapState(['plans', 'streams']),
    planWarnings() {
      return this.planList
          .map(p => ({id: p.id, text: this.warningFor(p)}))
          .filter(w => w.text);
    },
    planList() {
      return Object.entries(this.plans)
          .map(([k, v]) => ({...v, id: k}))
          .sort((a, b) => a.id.localeCompare(b.id))
    }
  },
  data() {
    return {
      headers:
          'id, stream, box, name, per_meal, large_price, alacarte, swap_at_plan_rate, ranges, action' // adjust,
              .split(', ')
              .map(s => ({text: s, value: s})),
      search: null,
      boxEditor: false,
      boxEditorPlanId: null,


    }
  }
}
</script>

<style scoped>

</style>